import api from "@/lib/axiosConfig";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

const baseRoute = "back/api/multipleprint";
const historyRoute = "back/api/multipleprint/history";

const _multipleprintHistorySchema = z.object({
  id: z.string(),
  estampesMultiplesHistoriqueDescriptionNom: z.string(),
  estampesMultiplesHistoriqueDescriptionVille: z.string(),
  estampesMultiplesHistoriqueDescriptionDates: z.string(),
  estampesMultiplesHistoriqueDescriptionCollectionPart: z.boolean(),
  multipleprintId: z.string().nullable(),
  updatedAt: z.string().optional(),
  createdAt: z.string().optional(),
  updatedById: z.string().optional().nullable(),
  createdById: z.string().optional().nullable(),
  provenanceRank: z.number().optional(),
});

export type MultipleprintHistory = z.infer<
  typeof _multipleprintHistorySchema
>;

export type MultipleprintHistoryMergedAuction = {
  kind: "auction";
  id: string;
  multipleprintId: string | null;
  instanceId: string;
  auctionId: string | null;
  provenanceRank?: number;
  auction: {
    id: string;
    ventesDescriptionNumeroVente: string;
    ventesDescriptionTitre: string;
    ventesDescriptionDate: string;
    ventesDescriptionLieu: string;
    ventesDescriptionVille: string;
    ventesDescriptionPays: string;
    ventesDescriptionCommissaires?: string;
    ventesPublicationStatus?: string;
  } | null;
  ventesDansEstampesMultiplesDescriptionNumeroLot: string;
  ventesDansEstampesMultiplesDescriptionNotes: string;
  createdAt?: string;
  updatedAt?: string | null;
};

export type MultipleprintHistoryMerged = {
  timeline: Array<
    (MultipleprintHistory & { kind: "history" }) |
      MultipleprintHistoryMergedAuction
  >;
};

async function getHistoryOfMultipleprint(multipleprintId: string) {
  const { data } = await api.get<MultipleprintHistory[]>(
    `${baseRoute}/${multipleprintId}/history`,
  );
  return data;
}

async function getHistoryOfMultipleprintMerged(multipleprintId: string) {
  const { data } = await api.get<MultipleprintHistoryMerged>(
    `${baseRoute}/${multipleprintId}/history`,
    {
      params: { includeAuctions: "true" },
    },
  );
  return data;
}

export function useGetHistoryOfMultipleprint(
  multipleprintId: string | undefined,
) {
  return useQuery({
    queryKey: ["multipleprint", multipleprintId, "history"],
    queryFn: () =>
      multipleprintId
        ? getHistoryOfMultipleprint(multipleprintId)
        : Promise.resolve([]),
    enabled: !!multipleprintId,
  });
}

export function useGetHistoryOfMultipleprintMerged(
  multipleprintId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["multipleprint", multipleprintId, "history", "withAuctions"],
    queryFn: () =>
      multipleprintId
        ? getHistoryOfMultipleprintMerged(multipleprintId)
        : Promise.resolve({ timeline: [] } as MultipleprintHistoryMerged),
    enabled: !!multipleprintId && enabled,
  });
}

const _createMultipleprintHistorySchema = _multipleprintHistorySchema.omit({
  id: true,
  multipleprintId: true,
  createdAt: true,
  createdById: true,
  updatedAt: true,
  updatedById: true,
});

export type CreateMultipleprintHistory = z.infer<
  typeof _createMultipleprintHistorySchema
>;

async function createMultipleprintHistory({
  history,
  multipleprintId,
}: {
  history: CreateMultipleprintHistory;
  multipleprintId: string;
}) {
  const { data } = await api.post(
    `${baseRoute}/${multipleprintId}/history`,
    history,
  );
  return data;
}

export function useCreateMultipleprintHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createMultipleprintHistory,
    onSuccess: (_, { multipleprintId }) => {
      queryClient.invalidateQueries({
        queryKey: ["multipleprint", multipleprintId, "history"],
      });
      queryClient.invalidateQueries({
        queryKey: ["multipleprint", multipleprintId],
      });
    },
  });
}

export type EditMultipleprintHistory = CreateMultipleprintHistory;

async function editMultipleprintHistory({
  history,
  historyId,
}: {
  history: EditMultipleprintHistory;
  historyId: string;
  multipleprintId: string;
}) {
  const { data } = await api.patch(`${historyRoute}/${historyId}`, history);
  return data;
}

export function useEditMultipleprintHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: editMultipleprintHistory,
    onSuccess: (_, { multipleprintId, historyId }) => {
      queryClient.invalidateQueries({
        queryKey: ["multipleprint-history", historyId],
      });
      queryClient.invalidateQueries({
        queryKey: ["multipleprint", multipleprintId, "history"],
      });
      queryClient.invalidateQueries({
        queryKey: ["multipleprint", multipleprintId],
      });
    },
  });
}

async function deleteMultipleprintHistory({
  historyId,
}: {
  historyId: string;
  multipleprintId: string;
}) {
  await api.delete(`${historyRoute}/${historyId}`);
}

export function useDeleteMultipleprintHistory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteMultipleprintHistory,
    onSuccess: (_, { multipleprintId, historyId }) => {
      queryClient.invalidateQueries({
        queryKey: ["multipleprint-history", historyId],
      });
      queryClient.invalidateQueries({
        queryKey: ["multipleprint", multipleprintId, "history"],
      });
      queryClient.invalidateQueries({
        queryKey: ["multipleprint", multipleprintId],
      });
    },
  });
}

async function updateMultipleprintHistorySequence({
  newSequence,
  multipleprintId,
}: {
  newSequence: string[];
  multipleprintId: string;
}) {
  await api.patch(`${baseRoute}/${multipleprintId}/history`, {
    sequence: newSequence,
  });
}

export function useUpdateMultipleprintHistorySequence() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateMultipleprintHistorySequence,
    onSuccess: (_, { multipleprintId }) => {
      queryClient.invalidateQueries({
        queryKey: ["multipleprint", multipleprintId, "history"],
      });
      queryClient.invalidateQueries({
        queryKey: ["multipleprint", multipleprintId],
      });
    },
  });
}

async function reorderProvenanceTimeline({
  multipleprintId,
  timeline,
}: {
  multipleprintId: string;
  timeline: { kind: "history" | "auction"; id: string }[];
}) {
  await api.patch(`${baseRoute}/${multipleprintId}/provenance-sequence`, {
    timeline,
  });
}

export function useReorderMultipleprintProvenance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: reorderProvenanceTimeline,
    onSuccess: (_, { multipleprintId }) => {
      queryClient.invalidateQueries({
        queryKey: ["multipleprint", multipleprintId, "history"],
      });
      queryClient.invalidateQueries({
        queryKey: ["multipleprint", multipleprintId],
      });
    },
  });
}
